import React, { useState, useEffect } from 'react';
import Card from '../../components/Common/Card';
import Table from '../../components/Common/Table';
import Select from '../../components/Common/Select';
import api from '../../utils/api';
import { DollarSign, Users, TrendingUp, Wallet } from 'lucide-react';
import { formatPrice } from '../../utils/formatters';
import { useNavigate } from 'react-router-dom';

const OfficeFinance = () => {
    const [loading, setLoading] = useState(true);
    const [data, setData] = useState([]);
    const [filters, setFilters] = useState({
        month: '',
        year: ''
    });

    const navigate = useNavigate();

    const months = [
        { value: '', label: 'Tüm Aylar' },
        { value: 1, label: 'Ocak' }, { value: 2, label: 'Şubat' }, { value: 3, label: 'Mart' },
        { value: 4, label: 'Nisan' }, { value: 5, label: 'Mayıs' }, { value: 6, label: 'Haziran' },
        { value: 7, label: 'Temmuz' }, { value: 8, label: 'Ağustos' }, { value: 9, label: 'Eylül' },
        { value: 10, label: 'Ekim' }, { value: 11, label: 'Kasım' }, { value: 12, label: 'Aralık' }
    ];

    const currentYear = new Date().getFullYear();
    const years = [
        { value: '', label: 'Tüm Yıllar' },
        ...Array.from({ length: 5 }, (_, i) => ({ value: currentYear - i, label: (currentYear - i).toString() }))
    ];

    useEffect(() => {
        fetchFinanceData();
    }, [filters]);

    const fetchFinanceData = async () => {
        try {
            setLoading(true);
            const response = await api.get('/finance/office', { params: filters });
            if (response.data.success) {
                setData(response.data.data);
            }
        } catch (error) {
            console.error('Finansal veriler yüklenemedi:', error);
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            header: 'Personel',
            render: (row) => (
                <span 
                    className="font-medium text-primary-600 dark:text-primary-400 hover:underline cursor-pointer"
                    onClick={() => navigate(`/departments/users/${row.id}`, { state: { activeTab: 'finance' } })}
                >
                    {row.name}
                </span>
            )
        },
        {
            header: 'İşlem Sayısı',
            render: (row) => <span className="text-center">{row.transaction_count}</span>
        },
        {
            header: 'Satış Tutarı',
            render: (row) => formatPrice(row.total_sales_amount)
        },
        {
            header: 'Müşteri Komisyonu',
            render: (row) => formatPrice(row.total_client_commission)
        },
        {
            header: 'Ofis Payı (Toplam)',
            render: (row) => (
                <div className="font-semibold text-primary-600 dark:text-primary-400">
                    {formatPrice(row.total_office_share)}
                </div>
            )
        },
        {
            header: 'Alınan Ödeme',
            render: (row) => (
                <span className="text-green-600 dark:text-green-400 font-medium">
                    {formatPrice(row.received_office_share)}
                </span>
            )
        },
        {
            header: 'Kalan Ödeme',
            render: (row) => (
                <span className={`font-bold ${row.remaining_office_share > 0 ? 'text-red-600 dark:text-red-400' : 'text-gray-500'}`}>
                    {formatPrice(row.remaining_office_share)}
                </span>
            )
        }
    ];

    // Özet toplamlar
    const totals = data.reduce((acc, curr) => ({
        transactions: acc.transactions + curr.transaction_count,
        sales: acc.sales + parseFloat(curr.total_sales_amount),
        commissions: acc.commissions + parseFloat(curr.total_client_commission),
        officeShareReceived: acc.officeShareReceived + parseFloat(curr.received_office_share || 0),
        officeShareRemaining: acc.officeShareRemaining + parseFloat(curr.remaining_office_share || 0)
    }), { transactions: 0, sales: 0, commissions: 0, officeShareReceived: 0, officeShareRemaining: 0 });

    return (
        <div className="space-y-6">
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold text-gray-800 dark:text-white">Ofis Finans Yönetimi</h1>
                    <p className="text-gray-600 dark:text-gray-400 mt-1">Personel bazlı finansal performans ve ofis payı takibi</p>
                </div>
                
                <div className="flex gap-2">
                    <Select
                        className="w-32"
                        value={filters.month}
                        onChange={(e) => setFilters({ ...filters, month: e.target.value })}
                        options={months}
                    />
                    <Select
                        className="w-24"
                        value={filters.year}
                        onChange={(e) => setFilters({ ...filters, year: e.target.value })}
                        options={years}
                    />
                </div>
            </div>

            {/* Özet Kartlar */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                <Card className="bg-primary-50 dark:bg-primary-900/10 border-primary-100 dark:border-primary-900/20">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-primary-600 text-white rounded-xl shadow-lg shadow-primary-200 dark:shadow-none">
                            <TrendingUp size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-primary-600 dark:text-primary-400 font-bold uppercase tracking-wider">Toplam İşlem</p>
                            <p className="text-2xl font-extrabold text-primary-900 dark:text-white">{totals.transactions}</p>
                        </div>
                    </div>
                </Card>

                <Card className="bg-blue-50 dark:bg-blue-900/10 border-blue-100 dark:border-blue-900/20">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-blue-600 text-white rounded-xl shadow-lg shadow-blue-200 dark:shadow-none">
                            <DollarSign size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-blue-600 dark:text-blue-400 font-bold uppercase tracking-wider">Satış Cirosu</p>
                            <p className="text-2xl font-extrabold text-blue-900 dark:text-white">{formatPrice(totals.sales)}</p>
                        </div>
                    </div>
                </Card>

                <Card className="bg-green-50 dark:bg-green-900/10 border-green-100 dark:border-green-900/20">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-green-600 text-white rounded-xl shadow-lg shadow-green-200 dark:shadow-none">
                            <Wallet size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-green-600 dark:text-green-400 font-bold uppercase tracking-wider">Alınan Ofis Payı</p>
                            <p className="text-2xl font-extrabold text-green-900 dark:text-white">{formatPrice(totals.officeShareReceived)}</p>
                        </div>
                    </div>
                </Card>

                <Card className="bg-red-50 dark:bg-red-900/10 border-red-100 dark:border-red-900/20">
                    <div className="flex items-center gap-4">
                        <div className="p-3 bg-red-600 text-white rounded-xl shadow-lg shadow-red-200 dark:shadow-none">
                            <Wallet size={24} />
                        </div>
                        <div>
                            <p className="text-sm text-red-600 dark:text-red-400 font-bold uppercase tracking-wider">Kalan Ofis Payı</p>
                            <p className="text-2xl font-extrabold text-red-900 dark:text-white">{formatPrice(totals.officeShareRemaining)}</p>
                        </div>
                    </div>
                </Card>
            </div>

            <Card noPadding>
                <Table
                    columns={columns}
                    data={data}
                    loading={loading}
                />
            </Card>
        </div>
    );
};

export default OfficeFinance;
